import React, { useEffect, useRef } from 'react';
import { XIcon } from '@heroicons/react/solid';
import NextScript from 'next/script';
import { useImageInput } from '@hooks/files/useImageInput';
import { useImageModalContext } from './ImageSelectModal';
import { GoogleDriveSVG } from '@components/base/svgs/GoogleDriveSVG';
import { GoogleTextlessSVG } from '@components/base/svgs/GoogleTextlessSVG';
import { AnimatedImageCropper, ImageModalFooter, MultiFileInputView } from './SharedComponents';
import { useFileTypeImage } from '@components/file/fileUtilities';
import { useDispatchError } from '@hooks/error/useDispatchError';

const API_KEY = process.env.NEXT_PUBLIC_GOOGLE_DRIVE_API_KEY;
const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;
const appId = process.env.NEXT_PUBLIC_GOOGLE_APP_ID;

const scopes = ['https://www.googleapis.com/auth/drive.file'];

class GoogleAuthWrapper {
    authScriptLoaded = false;
    pickerScriptLoaded = false;
    googleApi: any;
    Picker: any;
    Auth2: any;
    access_token: string | null = null;
    onSelectImage: (val: any) => void;
    getContentMimeTypes: (val?: any) => string | readonly string[];
    maxItems: number;
    mimeType: 'images' | 'video' | 'all';

    constructor({
        onSelectImage,
        getContentMimeTypes,
        maxItems,
        mimeType,
    }: {
        onSelectImage: (val: any) => void;
        getContentMimeTypes: (val?: any) => string | readonly string[];
        maxItems: number;
        mimeType: 'images' | 'video' | 'all';
    }) {
        this.onSelectImage = onSelectImage;
        this.getContentMimeTypes = getContentMimeTypes;
        this.maxItems = maxItems;
        this.mimeType = mimeType;
    }

    initializeClient = async () => {
        if ((this.authScriptLoaded && this.pickerScriptLoaded) || (window as any).gapi) {
            this.googleApi = (window as any).gapi;
            this.googleApi.auth2.authorize(
                {
                    immediate: false,
                    client_id: clientId,
                    scope: scopes.join(' '),
                },
                this.updateSignedInStatus,
            );
        } else {
            if (this.access_token) {
                this.buildGooglePicker();
            }
        }
    };

    onAuthApiLoaded = () => {
        this.authScriptLoaded = true;
    };

    onPickerLoaded = () => {
        this.pickerScriptLoaded = true;
    };

    updateSignedInStatus = (...state: any) => {
        this.access_token = state?.[0]?.access_token;
        if (this.access_token) {
            this.buildGooglePicker();
        }
    };

    onSelectFile = async (...params: any) => {
        if (params?.[0].action === 'picked') {
            const fileList = params?.[0]?.docs?.map(async (driveFile: any) => {
                const {
                    url: buildUrl,
                    name: fileName,
                    type: mimeType,
                } = constructDriveFileDownloadUrl(driveFile);
                const metaData = driveFile;
                const response = await fetch(buildUrl, {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${this.access_token}`,
                    },
                });
                if (response.status !== 200) return;

                const b = await response.blob();
                const d = new File([b], fileName, {
                    type: mimeType,
                });
                return d;
            });

            const fileResults = await Promise.all(fileList);

            this.onSelectImage(fileResults);
            if (this.Picker) {
                this.Picker?.setVisible?.(false);
                this.Picker?.dispose?.();
            }
        }
    };

    /**
     * API Docs can be found at: https://developers.google.com/drive/picker/reference#view-id
     *
     */
    buildGooglePicker = () => {
        let googleApi = (window as any).gapi;
        let googlePicker = googleApi?.picker?.api;
        if (googlePicker) {
            let picker: any = {};
            const mimeTypes = this.getContentMimeTypes();

            const viewerBase =
                this.mimeType === 'video'
                    ? googlePicker.ViewId.DOCS_VIDEOS
                    : this.mimeType === 'images'
                    ? googlePicker.ViewId.DOCS_IMAGES
                    : googlePicker.ViewId.FOLDERS; // a hack to get all files. Somehow googlePicker.ViewId.DOCS doesn't work

            const view = new googlePicker.View(viewerBase);
            if (mimeTypes) {
                view.setMimeTypes(mimeTypes);
            }

            picker = new googlePicker.PickerBuilder()
                .setMaxItems(this.maxItems || 1)
                .addView(view)
                .setAppId(appId)
                .setOAuthToken(this.access_token)
                .setDeveloperKey(API_KEY)
                .setCallback(this.onSelectFile);

            if (this.maxItems > 1) {
                picker.enableFeature(googlePicker.Feature.MULTISELECT_ENABLED);
            }

            if (mimeTypes && mimeTypes != '*/*') {
                picker.setSelectableMimeTypes(mimeTypes);
            }
            picker = picker.build();

            picker.setVisible(true);
            this.Picker = picker;
        }
    };
}

export function UploadGoogleDrive({ toggle }: { toggle: any }) {
    const {
        fileCreate,
        authorizer,
        getContentMimeTypes,
        fileList,
        isMultiFile,
        isImmediateUpdate,
        allowedFileTypes,
    } = useImageModalContext();
    const dispatchError = useDispatchError();
    const { fileInput, croppedInput, crop, handlers } = useImageInput({
        immediateUpdate: isImmediateUpdate,
        updateNow: (file) => {
            if (file)
                fileCreate.mutate({
                    title: file.name ?? `file-${Date.now()}`,
                    file: file,
                    authorizerItem: authorizer,
                });
            else
                dispatchError(
                    'Some of the selected files could not be downloaded from your Google Drive.',
                );
        },
        multiple: isMultiFile,
    });
    const GoogRefClass = useRef(
        new GoogleAuthWrapper({
            onSelectImage: handlers.createFilesAll,
            getContentMimeTypes,
            maxItems: isMultiFile ? 5 : 1,
            mimeType:
                allowedFileTypes === 'images'
                    ? 'images'
                    : allowedFileTypes === 'video'
                    ? 'video'
                    : 'all',
        }),
    ).current;

    const selectIcon = useFileTypeImage();
    useEffect(() => {
        GoogRefClass.initializeClient();
    }, []);

    const showMultiple = isMultiFile && !!fileList?.length;
    const showSingle = !isMultiFile && !!fileInput;
    const showDefault = !showMultiple && !showSingle;

    return (
        <div className="relative flex flex-col flex-1 overflow-hidden">
            <NextScript
                id="googlePicker"
                src="https://apis.google.com/js/api.js"
                onLoad={() => {
                    const Gapi = (window as any).gapi;
                    Gapi.load('client:auth2', GoogRefClass.onAuthApiLoaded);
                    Gapi.load('picker', GoogRefClass.onPickerLoaded);
                }}
            />
            <div className="w-full h-full flex flex-1 flex-col">
                <div className="relative px-4 py-3 flex items-center justify-center border-b border-gray-200">
                    <div>
                        <GoogleDriveSVG className="fill-current text-white w-8 h-8" />
                    </div>

                    <button
                        type="button"
                        onClick={() => toggle(false)}
                        className="absolute px-3 flex items-center justify-center inset-y right-2 focus:outline-primary"
                    >
                        <XIcon className="w-5 h-5 fill-gray-500 stroke-gray-500 hover:fill-teal-900 hover:stroke-teal-900 font-bold" />
                    </button>
                </div>
                <div className="flex-1 flex flex-col items-center justify-center w-full">
                    <div className="flex flex-col items-center justify-center flex-1 w-full">
                        {showSingle && (
                            <AnimatedImageCropper
                                selected={{ url: fileInput?.localUrl, ...fileInput }}
                                handlers={handlers}
                                crop={crop}
                                close={close}
                                onClickHeader={() => toggle(false)}
                            />
                        )}

                        {showMultiple && (
                            <div className="relative w-full h-full flex-col justify-center items-center grid grid-cols-8">
                                <MultiFileInputView
                                    fileList={fileList}
                                    fileCreate={fileCreate}
                                    selectIcon={selectIcon}
                                />
                            </div>
                        )}
                        {showDefault && (
                            <div className="w-full h-full flex flex-col items-center justify-center">
                                <div className="p-3 rounded-full">
                                    <GoogleDriveSVG className="fill-current text-white w-12 h-12" />
                                </div>
                                <div className="py-8">
                                    <h2 className="text-2xl">Select Files from Google Drive</h2>
                                </div>
                                <div className="flex flex-col text-center gap-y-1 text-gray-500">
                                    <p>
                                        We only extract selected files and never modify or delete
                                        them.
                                    </p>
                                </div>
                                <div className="text-center flex items-center py-6">
                                    <button
                                        type="button"
                                        onClick={() => GoogRefClass.initializeClient()}
                                        className="text-center focus:outline-none text-gray-700  px-4 py-3 rounded-sm border border-gray-300 flex items-center gap-x-4"
                                    >
                                        <GoogleTextlessSVG />
                                        Select Files from Google
                                    </button>
                                </div>
                                <div className="flex flex-col text-center gap-y-1 text-gray-500">
                                    <p>A popup will open to select files from your Google Drive.</p>
                                    <p>
                                        You may need to sign in or choose a Google account to
                                        proceed.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <ImageModalFooter />
        </div>
    );
}

const GOOGLE_APPS_MIME_PREFIX = 'application/vnd.google-apps.';

const constructDriveFileDownloadUrl = (driveFile: any) => {
    const mimeType = driveFile?.mimeType ?? '';

    // Binary Drive files can be downloaded directly. Docs Editors files must be exported.
    if (!mimeType.startsWith(GOOGLE_APPS_MIME_PREFIX)) {
        return {
            url: `https://www.googleapis.com/drive/v3/files/${driveFile?.id}?alt=media`,
            name: driveFile?.name,
            type: mimeType,
        };
    }

    switch (mimeType) {
        case 'application/vnd.google-apps.document':
            return {
                url: `https://www.googleapis.com/drive/v3/files/${driveFile?.id}/export?mimeType=application/vnd.openxmlformats-officedocument.wordprocessingml.document&alt=media`,
                name: driveFile?.name + (driveFile?.name?.endsWith('.docx') ? '' : '.docx'),
                type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            };
        case 'application/vnd.google-apps.spreadsheet':
            return {
                url: `https://www.googleapis.com/drive/v3/files/${driveFile?.id}/export?mimeType=application/vnd.openxmlformats-officedocument.spreadsheetml.sheet&alt=media`,
                name: driveFile?.name + (driveFile?.name?.endsWith('.xlsx') ? '' : '.xlsx'),
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            };
        case 'application/vnd.google-apps.presentation':
            return {
                url: `https://www.googleapis.com/drive/v3/files/${driveFile?.id}/export?mimeType=application/vnd.openxmlformats-officedocument.presentationml.presentation&alt=media`,
                name: driveFile?.name + (driveFile?.name?.endsWith('.pptx') ? '' : '.pptx'),
                type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            };
        default:
            // or try export as PDF for other types
            return {
                url: `https://www.googleapis.com/drive/v3/files/${driveFile?.id}/export?mimeType=application/pdf&alt=media`,
                name: driveFile?.name + '.pdf',
                type: 'application/pdf',
            };
    }
};
